
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { InputType } from '@/enums/InputType';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import StackedForm from '@/mixins/StackedForm';
import DeliverableOrderType from '@/mixins/DeliverableOrderType';
import { DeliveryProviders } from '@/interfaces/models/DeliveryProviders';
import { Permission } from '@/enums/Permission';
import { DeliverableOrderTypes as DeliverableOrderTypesEnum } from '@/enums/DeliverableOrderTypes';
import { CustomerGroup } from '@/enums/CustomerGroup';
import { Venue } from '@/interfaces/models/Venue';
import { UberDirectProvider } from '@/interfaces/models/UberDirectProvider';
import { OrderLordProvider } from '@/interfaces/models/orderLord/OrderLordProvider';
import { WoltDriveProvider } from '@/interfaces/models/woltDrive/WoltDriveProvider';
import { UltraDispoProvider } from '@/interfaces/models/ultraDispo/UltraDispoProvider';
import { DishbeeProvider } from '@/interfaces/models/dishbee/DishbeeProvider';
import { KurierzentraleProvider } from '@/interfaces/models/kurierzentrale/KurierzentraleProvider';

@Component({
  components: { VFormBuilder },
})
export default class DeliverProvidersForm extends mixins(StackedForm, DeliverableOrderType) {
  public $refs!: {
    smoothrDispatch: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    orderLord: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    rushHour: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    uberDelivery: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    woltDrive: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    ultraDispo: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    dishbee: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
    kurierzentrale: InstanceType<typeof VFormBuilder> & {
      form: any;
    };
  };

  get initValues() {
    const venue: Venue = this.initialValues as Venue;

    return venue?.deliveryProviders;
  }

  get initOrderLord() {
    const values: DeliveryProviders = { ...this.initValues } as DeliveryProviders;

    return {
      orderLordActive: !!(values.orderLord && values.orderLord.orderLordActive),
      orderLordEnabled: !!(values.orderLord && values.orderLord.orderLordEnabled),
      orderLordToken: values.orderLord && values.orderLord.orderLordToken,
      orderLordPassword: values.orderLord && values.orderLord.orderLordPassword,
    };
  }

  get initSmoothrDispatch() {
    const values: DeliveryProviders = { ...this.initValues } as DeliveryProviders;

    if (!values?.smoothrDispatch) {
      return { enabled: !!(this.initialValues as any)?.smoothrDispatchEnabled };
    }

    return { ...values.smoothrDispatch };
  }

  get initUberDelivery() {
    const values: DeliveryProviders = { ...this.initValues } as DeliveryProviders;

    if (!values?.uberDirect) {
      return {
        enabled: !!(this.initialValues as Venue)?.uberDeliveryEnabled,
        customerId: (this.initialValues as Venue)?.uberCustomerId,
        pinVerificationEnabled: false,
        pinVerificationMinimumAmount: null,
      };
    }

    return {
      ...values.uberDirect,
      pinVerificationEnabled: values.uberDirect.pinVerification?.enabled ?? false,
      pinVerificationMinimumAmount: values.uberDirect.pinVerification?.minimumAmount ?? null,
    };
  }

  get isDeanAndDavidGroup() {
    const venue: Venue = this.initialValues as Venue;

    return venue?.customerGroup === CustomerGroup.DeanAndDavid;
  }

  get smoothrDispatchItems() {
    return [
      {
        name: 'enabled',
        type: InputType.Checkbox,
        default: null,
        label: 'venue.form.deliveryProviders.enabled',
      },
    ];
  }

  get orderLordItems() {
    return [
      {
        name: 'orderLordEnabled',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.orderLordEnabled',
        visible: this.$can(Permission.DELIVERY_PROVIDER_ENABLING),
      },
      {
        name: 'orderLordActive',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.orderLordActive',
        visible: this.$can(Permission.DELIVERY_PROVIDER_ACTIVATION),
      },
      {
        name: 'orderLordToken',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.orderLordToken',
        rules: (form: OrderLordProvider) => (form.orderLordEnabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'orderLordPassword',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.orderLordPassword',
        rules: (form: OrderLordProvider) => (form.orderLordEnabled ? 'required' : ''),
        autocomplete: 'off',
      },
    ];
  }

  get rushHourItems() {
    return [
      {
        name: 'rushhourIntegration',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.rushhourIntegration',
        visible: this.$can(Permission.DELIVERY_PROVIDER_ACTIVATION),
      },
      {
        name: 'rushhourToken',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.rushhourToken',
        autocomplete: 'off',
      },
    ];
  }

  get woltDriveItems() {
    return [
      {
        name: 'enabled',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.enabled',
      },
      {
        name: 'merchantId',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.woltdriveMerchantId',
        rules: (form: WoltDriveProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'merchantSecretToken',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.woltdriveToken',
        rules: (form: WoltDriveProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
    ];
  }

  get uberDeliveryItems() {
    return [
      {
        name: 'customerId',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.customerId',
        rules: (form: UberDirectProvider) => (form.enabled ? 'required' : ''),
      },
      {
        name: 'clientId',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.clientId',
        rules: (form: UberDirectProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'clientSecret',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.clientSecret',
        rules: (form: UberDirectProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'enabled',
        type: InputType.Checkbox,
        default: null,
        label: 'venue.form.deliveryProviders.enabled',
      },
      {
        name: 'batchSize',
        type: InputType.Text,
        default: 1,
        rules: 'integer|min:1',
        label: 'venue.form.deliveryProviders.batchSize',
      },
      {
        name: 'deliverableOrderTypes',
        type: InputType.Autocomplete,
        label: 'venue.form.deliveryProviders.deliverableOrderTypes',
        useChips: true,
        multiple: true,
        clearable: true,
        items: this.deliverableOrderTypes,
        visible: this.isDeanAndDavidGroup,
        default: this.isDeanAndDavidGroup
          ? [DeliverableOrderTypesEnum.FOODSPOT, DeliverableOrderTypesEnum.DELIVERY]
          : [DeliverableOrderTypesEnum.DELIVERY],
      },
      {
        name: 'pinVerificationEnabled',
        type: InputType.Checkbox,
        default: false,
        label: 'venue.form.deliveryProviders.pinVerificationEnabled',
      },
      {
        name: 'pinVerificationMinimumAmount',
        type: InputType.Decimal,
        rules: 'required|min_value:0.01',
        label: 'venue.form.deliveryProviders.pinVerificationMinimumAmount',
        visible: (form: any) => form.pinVerificationEnabled,
      },
    ];
  }

  get ultraDispoItems() {
    return [
      {
        name: 'enabled',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.enabled',
        visible: this.$can(Permission.DELIVERY_PROVIDER_ENABLING),
      },
      {
        name: 'merchantUsername',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.merchantUsername',
        rules: (form: UltraDispoProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'merchantPassword',
        type: InputType.Password,
        label: 'venue.form.deliveryProviders.merchantPassword',
        rules: (form: UltraDispoProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'new-password',
      },
    ];
  }

  get dishbeeItems() {
    return [
      {
        name: 'enabled',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.enabled',
      },
      {
        name: 'botToken',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.botToken',
        rules: (form: DishbeeProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'channelId',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.channelId',
        rules: (form: DishbeeProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
    ];
  }

  get kurierzentraleItems() {
    return [
      {
        name: 'enabled',
        type: InputType.Checkbox,
        label: 'venue.form.deliveryProviders.enabled',
      },
      {
        name: 'username',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.username',
        rules: (form: KurierzentraleProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'password',
        type: InputType.Password,
        label: 'venue.form.deliveryProviders.password',
        rules: (form: KurierzentraleProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'new-password',
      },
      {
        name: 'partnerId',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.partnerId',
        rules: (form: KurierzentraleProvider) => (form.enabled ? 'required' : ''),
        autocomplete: 'off',
      },
      {
        name: 'averageDeliveryTime',
        type: InputType.Text,
        label: 'venue.form.deliveryProviders.averageDeliveryTime',
        rules: (form: KurierzentraleProvider) =>
          form.enabled ? 'required|integer|greater_than:0' : 'integer|greater_than:0',
      },
    ];
  }

  public getData() {
    const { pinVerificationEnabled, pinVerificationMinimumAmount, ...rest } = this.$refs.uberDelivery?.form || {};
    const uberDeliveryData = {
      ...rest,
      pinVerification: pinVerificationEnabled
        ? {
            enabled: pinVerificationEnabled,
            minimumAmount: pinVerificationMinimumAmount,
          }
        : undefined,
    };

    return {
      smoothrDispatchEnabled: this.$refs?.smoothrDispatch?.form?.enabled,
      uberCustomerId: this.$refs?.uberDelivery?.form?.customerId,
      uberDeliveryEnabled: this.$refs?.uberDelivery?.form?.enabled,
      deliveryProviders: {
        useDeliveryProvider: this.useDeliveryProvider(),
        smoothrDispatch: {
          ...this.$refs?.smoothrDispatch?.form,
        },
        orderLord: {
          ...this.$refs.orderLord?.form,
        },
        rushHour: {
          ...this.$refs.rushHour?.form,
        },
        uberDirect: {
          ...uberDeliveryData,
        },
        woltDrive: {
          ...this.$refs.woltDrive?.form,
        },
        ultraDispo: {
          ...this.$refs.ultraDispo?.form,
        },
        dishbee: {
          ...this.$refs.dishbee?.form,
        },
        kurierzentrale: {
          ...this.$refs.kurierzentrale?.form,
        },
      },
    };
  }

  private useDeliveryProvider() {
    return (
      this.$refs?.orderLord?.form?.orderLordEnabled ||
      this.$refs?.rushHour?.form?.rushhourIntegration ||
      this.$refs.uberDelivery?.form?.enabled ||
      this.$refs?.woltDrive?.form?.enabled ||
      this.$refs?.ultraDispo?.form?.enabled ||
      this.$refs?.dishbee?.form?.enabled ||
      this.$refs?.kurierzentrale?.form?.enabled
    );
  }
}
